// SPDX-License-Identifier: Elastic-2.0
/**
 * Stepper — dot progress indicator.
 *
 * Renders `steps` dots in a row. The dot at index `current` widens to a
 * pill; previously-visited dots take the foreground tint; remaining dots
 * stay at low-alpha. Visual spec lifted from `WizardShell.Stepper`.
 *
 * Accessibility: the row itself is labelled `"Step N of M"` via
 * `aria-label`; individual dots are decorative (`aria-hidden`) because
 * the announcement is already in the parent.
 */
import styles from "./Stepper.module.css";

export interface StepperProps {
  /** Total number of steps. Must be ≥ 1. */
  steps: number;
  /** 0-indexed position of the current step. Clamped to `[0, steps - 1]`. */
  current: number;
}

export function Stepper({ steps, current }: StepperProps): JSX.Element {
  // Defensive clamping — out-of-range `current` should still render
  // something sensible rather than producing no current dot.
  const safeSteps = Math.max(1, Math.floor(steps));
  const safeCurrent = Math.min(Math.max(0, Math.floor(current)), safeSteps - 1);

  return (
    <div
      role="progressbar"
      aria-label={`Step ${safeCurrent + 1} of ${safeSteps}`}
      aria-valuenow={safeCurrent + 1}
      aria-valuemin={1}
      aria-valuemax={safeSteps}
      className={styles.root}
      data-testid="stepper"
    >
      {Array.from({ length: safeSteps }).map((_, i) => {
        const isCurrent = i === safeCurrent;
        const isVisited = i < safeCurrent;
        const className = [
          styles.dot,
          isVisited ? styles.dotVisited : "",
          isCurrent ? styles.dotCurrent : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <span
            key={i}
            aria-hidden="true"
            data-testid={`stepper-dot-${i}`}
            data-state={isCurrent ? "current" : isVisited ? "visited" : "pending"}
            className={className}
          />
        );
      })}
    </div>
  );
}
